import { useRef, useState } from "react";
import { useSuspenseQueries, useSuspenseQuery } from "@tanstack/react-query";
import { CredentialCategory, CredentialType } from "@api/shared-runtime/enums";
import type { ProviderUser } from "@/common/types/ProviderUser";
import { cn } from "@/lib/utils";
import { useTRPC } from "@/trpc";
import { ProfileHero } from "@/features/profile/components/profile-overview/profile-hero";
import { ProfileJobPreferences } from "@/features/profile/components/profile-overview/profile-job-preferences";
import { ProfileCredentialsPanel } from "@/features/profile/components/profile-overview/profile-credentials-panel";
import {
  ProfileEducation,
  ProfileLicenses,
  ProfileWorkHistory,
} from "@/features/profile/components/profile-overview/profile-record-sections";
import { useProviderCredentialEditing } from "../hooks/use-provider-credential-editing";
import { blankCredentialInstance } from "../lib/blank-credential-instance";

const NEW_PROFILE_CREDENTIAL_SUFFIX = "profile-new";

/** The next profile experience, kept on the unreleased credentials route. */
export function CredentialsPage({ user }: { user: ProviderUser }) {
  const trpc = useTRPC();
  // The route loads both queries before mounting this page. Suspense keeps any
  // later cache miss in the same route loading/error boundary, while cached
  // data remains visible if a background refresh fails during an edit.
  const credentials = useSuspenseQuery(
    trpc.credentials.getMyCredentials.queryOptions({ switchUserId: user.id }),
  );
  const preferenceQueries = user.jobPostsEnabled
    ? [trpc.profile.getJobPreferences.queryOptions({ userId: user.id })]
    : [];
  const [jobPreferences] = useSuspenseQueries({ queries: preferenceQueries });
  const [panelOpen, setPanelOpen] = useState(false);
  const editing = useProviderCredentialEditing({
    userId: user.id,
    data: credentials.data,
    enabled: panelOpen,
    uploadPresentation: "dropzone",
  });
  const panelOpenerRef = useRef<HTMLElement | null>(null);
  const [initialSection, setInitialSection] = useState<string>();

  function openCredentials(section?: string) {
    // The editor and sheet may open together from a profile action. Capture
    // that action before either dialog moves focus into its own content.
    panelOpenerRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setInitialSection(section);
    setPanelOpen(true);
  }

  function editCredential({
    type,
    createNew = false,
  }: {
    type: CredentialType;
    createNew?: boolean;
  }) {
    const template = credentials.data.credentials.find(
      (credential) => credential.credentialType === type,
    );
    if (!template) {
      return;
    }

    const credential = createNew
      ? blankCredentialInstance(template, NEW_PROFILE_CREDENTIAL_SUFFIX)
      : template;
    openCredentials(credential.category);
    editing.editCredential(credential);
  }

  function handlePanelOpenChange(open: boolean) {
    // Nested dialogs own their close flow, including unsaved-change prompts.
    // A parent dismissal must never unmount an editor or an in-flight save.
    if (!open && editing.isDialogOpen) {
      return;
    }
    setPanelOpen(open);
    if (!open) {
      editing.close();
    }
  }

  return (
    <div className="min-h-full bg-slate-50">
      <div className="mx-auto w-full max-w-[1280px] space-y-6 px-4 pt-4 pb-6 sm:px-6 sm:pt-6 sm:pb-8 xl:space-y-8 xl:px-8">
        <ProfileHero
          user={user}
          credentials={credentials.data.credentials}
          jobPreferences={jobPreferences?.data}
          onOpenCredentials={() => openCredentials()}
          onEditProfile={() =>
            editCredential({ type: CredentialType.PersonalInfo })
          }
        />
        <div
          className={cn(
            "grid min-w-0 grid-cols-1 gap-6 xl:gap-8",
            user.jobPostsEnabled &&
              "xl:grid-cols-[minmax(0,2fr)_minmax(260px,1fr)]",
          )}
        >
          {user.jobPostsEnabled && (
            <aside className="min-w-0 xl:order-last">
              <ProfileJobPreferences
                userId={user.id}
                preferences={jobPreferences?.data}
              />
            </aside>
          )}
          <div className="min-w-0 space-y-6 sm:space-y-8">
            <ProfileLicenses
              credentials={credentials.data.credentials}
              onOpen={() => openCredentials(CredentialCategory.Licenses)}
              onAdd={() =>
                editCredential({
                  type: CredentialType.StateLicense,
                  createNew: true,
                })
              }
            />
            <ProfileWorkHistory
              credentials={credentials.data.credentials}
              onAdd={() =>
                editCredential({
                  type: CredentialType.WorkHistory,
                  createNew: true,
                })
              }
            />
            <ProfileEducation
              credentials={credentials.data.credentials}
              onOpen={() => openCredentials(CredentialCategory.Education)}
              onAdd={() =>
                editCredential({
                  type: CredentialType.Education,
                  createNew: true,
                })
              }
            />
          </div>
        </div>
      </div>
      <ProfileCredentialsPanel
        userId={user.id}
        data={credentials.data}
        open={panelOpen}
        onOpenChange={handlePanelOpenChange}
        initialSection={initialSection}
        cardActions={editing.cardActions}
        openerRef={panelOpenerRef}
        notice={editing.notice}
        filesView={editing.filesView}
        closeDisabled={editing.isDialogOpen}
        summaryActions={editing.summaryActions}
        uploadArea={editing.uploadArea}
        sectionContent={editing.sectionContent}
        sectionBodies={editing.sectionBodies}
      >
        {editing.modals}
      </ProfileCredentialsPanel>
    </div>
  );
}
